import { account } from '../core/account.js';
import { i18n, t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml, formatDate } from '../core/utils.js';

/* Panel de administración (solo admins): lista de correos autorizados a entrar en Filoteca. */

let seq = 0;

function userRow(u) {
    const me = account.user && u.email === account.user.email;
    return `<div class="admin-user ${u.active ? '' : 'is-inactive'}" data-email="${escapeHtml(u.email)}">
        ${u.picture ? `<img class="admin-avatar" src="${escapeHtml(u.picture)}" alt="" referrerpolicy="no-referrer"/>` : `<span class="admin-avatar mi">person</span>`}
        <div class="admin-user-body">
            <div class="admin-user-name">${escapeHtml(u.name || u.email)}${u.role === 'admin' ? ` <span class="tag">${t('admin.roleAdmin')}</span>` : ''}${u.active ? '' : ` <span class="tag tag-off">${t('admin.inactive')}</span>`}</div>
            <div class="admin-user-sub">${u.name ? escapeHtml(u.email) + ' · ' : ''}${u.lastLogin ? t('admin.lastLogin', { date: formatDate(u.lastLogin, i18n.lang) }) : t('admin.never')}</div>
        </div>
        ${me ? '' : `<button type="button" class="btn btn-icon" data-user-menu aria-label="${t('common.info')}"><span class="mi">more_vert</span></button>`}
    </div>`;
}

function inviteRow(i) {
    return `<div class="admin-user ${i.status === 'active' ? '' : 'is-inactive'}" data-invite-id="${i.id}">
        <span class="admin-avatar mi">link</span>
        <div class="admin-user-body">
            <div class="admin-user-name">${escapeHtml(i.note || t('admin.inviteUnnamed', { id: '#' + i.id }))} <span class="tag ${i.status === 'active' ? '' : 'tag-off'}">${t('admin.inviteStatus.' + i.status)}</span></div>
            <div class="admin-user-sub">${t('admin.inviteUsage', { uses: i.uses, max: i.maxUses, date: formatDate(i.expiresAt, i18n.lang) })}</div>
        </div>
        ${i.status === 'active' ? `<button type="button" class="btn btn-icon" data-invite-menu aria-label="${t('common.info')}"><span class="mi">more_vert</span></button>` : ''}
    </div>`;
}

function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
    return new Promise((resolve, reject) => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy') ? resolve() : reject(new Error('copy')); } catch (e) { reject(e); } finally { document.body.removeChild(ta); }
    });
}

/** Formulario de una invitación nueva y, al crearla, el enlace (que solo se ve entonces). */
function newInviteDialog(onCreated) {
    const app = window.app;
    const opts = (list) => list.map(([v, label]) => `<option value="${v}">${escapeHtml(label)}</option>`).join('');
    const dialog = app.dialog.create({
        title: t('admin.inviteTitle'),
        content: `<form class="pw-dialog" novalidate>
            <div class="field"><label>${t('admin.inviteValid')}</label><select name="days">${opts([[1, t('admin.inviteDay1')], [7, t('admin.inviteDays', { n: 7 })], [30, t('admin.inviteDays', { n: 30 })]]).replace('value="7"', 'value="7" selected')}</select></div>
            <div class="field"><label>${t('admin.inviteUses')}</label><select name="uses">${opts([[1, t('admin.inviteUses1')], [5, t('admin.inviteUsesN', { n: 5 })], [20, t('admin.inviteUsesN', { n: 20 })]])}</select></div>
            <div class="field"><label>${t('admin.inviteNote')}</label><input name="note" maxlength="80" autocomplete="off"/></div>
            <div class="field-error" data-pw-error hidden></div>
        </form>`,
        closeByBackdropClick: false,
        buttons: [
            { text: t('common.cancel') },
            {
                text: t('admin.inviteCreate'),
                bold: true,
                close: false,
                onClick: async (d) => {
                    const form = d.$el[0].querySelector('form');
                    try {
                        const made = await account.createInvite({ days: Number(form.days.value), uses: Number(form.uses.value), note: form.note.value.trim() });
                        d.close();
                        showInviteLink(made.url);
                        onCreated();
                    } catch (e) {
                        const err = form.querySelector('[data-pw-error]');
                        err.textContent = t('common.errorNetwork');
                        err.hidden = false;
                    }
                }
            }
        ]
    });
    dialog.open();
}

function showInviteLink(url) {
    const app = window.app;
    const canShare = !!navigator.share;
    const buttons = [
        {
            text: t('admin.inviteCopy'),
            bold: true,
            close: false,
            onClick: () => copyText(url).then(() => notify.toast(t('admin.inviteCopied'), { icon: 'check' }), () => {
                const input = document.querySelector('.dialog.modal-in [data-invite-url]');
                if (input) { input.focus(); input.select(); }
            })
        }
    ];
    if (canShare) buttons.push({ text: t('admin.inviteShare'), close: false, onClick: () => navigator.share({ text: t('admin.inviteShareText'), url }).catch(() => {}) });
    buttons.push({ text: t('common.close') });
    app.dialog.create({
        title: t('admin.inviteLinkTitle'),
        content: `<div class="pw-dialog"><p class="pw-note">${t('admin.inviteLinkHelp')}</p><div class="field"><input readonly data-invite-url value="${escapeHtml(url)}" onfocus="this.select()"/></div></div>`,
        closeByBackdropClick: false,
        buttons
    }).open();
}

export function openUsersAdmin() {
    const app = window.app;
    const id = 'admin-' + (++seq) + '-' + Date.now().toString(36);
    const html = `<div class="popup catpick-popup admin-popup" id="${id}">
        <div class="view">
            <div class="page">
                <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                    <div class="title">${t('admin.title')}</div>
                    <div class="right"><a href="#" class="link popup-close" aria-label="${t('common.close')}"><span class="mi">close</span></a></div>
                </div></div>
                <div class="page-content">
                    <form class="admin-add" data-add novalidate>
                        <input type="email" name="email" placeholder="${t('admin.emailPh')}" autocomplete="off" autocapitalize="off" inputmode="email" data-nav-default/>
                        <button type="submit" class="btn btn-accent"><span class="mi">person_add</span>${t('admin.addUser')}</button>
                    </form>
                    <div class="field-error" data-error hidden></div>
                    <div class="admin-list" data-list><div class="spinner"></div></div>
                    <div class="admin-section">
                        <div class="admin-section-head">
                            <div><h3>${t('admin.invites')}</h3><small>${t('admin.inviteHelp')}</small></div>
                            <button type="button" class="btn btn-accent" data-invite-new><span class="mi">add_link</span>${t('admin.inviteNew')}</button>
                        </div>
                        <div class="admin-list" data-invites></div>
                    </div>
                </div>
            </div>
        </div>
    </div>`;

    let users = [];
    let invites = [];
    const popup = app.popup.create({
        content: html,
        on: {
            open(p) {
                const el = p.el;
                const list = el.querySelector('[data-list]');
                const errorEl = el.querySelector('[data-error]');
                const form = el.querySelector('[data-add]');
                const showError = (msg) => { errorEl.textContent = msg; errorEl.hidden = !msg; };
                const inviteList = el.querySelector('[data-invites]');
                const paint = () => {
                    list.innerHTML = users.map(userRow).join('');
                    inviteList.innerHTML = invites.length ? invites.map(inviteRow).join('') : `<p class="pw-note">${t('admin.inviteEmpty')}</p>`;
                };
                const load = async () => {
                    try {
                        users = await account.listUsers();
                        invites = await account.listInvites().catch(() => []);
                        paint();
                    } catch (e) {
                        list.innerHTML = '';
                        showError(t('common.errorNetwork'));
                    }
                };
                load();

                form.addEventListener('submit', async (e) => {
                    e.preventDefault();
                    const email = form.email.value.trim();
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return showError(t('admin.invalidEmail'));
                    showError('');
                    try {
                        await account.addUser(email, 'user');
                        form.email.value = '';
                        notify.toast(t('admin.added'), { icon: 'check' });
                        await load();
                    } catch (err) {
                        showError(err.code === 'invalid_email' ? t('admin.invalidEmail') : t('common.errorNetwork'));
                    }
                });

                el.querySelector('[data-invite-new]').addEventListener('click', () => newInviteDialog(load));
                inviteList.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-invite-menu]');
                    if (!btn) return;
                    const inviteId = btn.closest('[data-invite-id]').getAttribute('data-invite-id');
                    app.actions.create({ buttons: [[
                        { text: t('admin.inviteRevoke'), color: 'red', onClick: () => notify.confirm(t('admin.inviteRevokeConfirm'), t('admin.inviteRevoke'), async () => {
                            try { await account.revokeInvite(inviteId); notify.toast(t('admin.inviteRevoked'), { icon: 'check' }); await load(); } catch (err) { notify.error(t('common.errorNetwork')); }
                        }) }
                    ], [{ text: t('common.cancel'), color: 'red' }]] }).open();
                });

                list.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-user-menu]');
                    if (!btn) return;
                    const email = btn.closest('[data-email]').getAttribute('data-email');
                    const u = users.find((x) => x.email === email);
                    if (!u) return;
                    const run = (fn) => async () => {
                        try { await fn(); await load(); } catch (err) { notify.error(t('common.errorNetwork')); }
                    };
                    app.actions.create({ buttons: [[
                        { text: u.email, label: true },
                        { text: u.active ? t('admin.disable') : t('admin.enable'), onClick: run(() => account.updateUser(email, { active: !u.active })) },
                        { text: u.role === 'admin' ? t('admin.makeUser') : t('admin.makeAdmin'), onClick: run(() => account.updateUser(email, { role: u.role === 'admin' ? 'user' : 'admin' })) },
                        { text: t('admin.remove'), color: 'red', onClick: () => notify.confirm(t('admin.removeConfirm', { email }), t('admin.remove'), run(() => account.removeUser(email))) }
                    ], [{ text: t('common.cancel'), color: 'red' }]] }).open();
                });
            },
            opened(p) {
                if (window.IPTV.device.isTV) window.IPTV.tvnav.focusLayer(p.el);
            },
            closed() {
                try { popup.destroy(); } catch (e) { /* ya destruido */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}
